import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainMxRecords, setDomainMxRecords] = useState({});
  const [realm, setRealm] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [tenantRegion, setTenantRegion] = useState(null);
  const [adConnectEnabled, setAdConnectEnabled] = useState(null);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTimer, setCooldownTimer] = useState(0);

  useEffect(() => {
    let interval;
    if (cooldown && cooldownTimer > 0) {
      interval = setInterval(() => {
        setCooldownTimer((prev) => {
          if (prev <= 1) {
            setCooldown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [cooldown, cooldownTimer]);

  const lookupMxRecords = async (domainName) => {
    try {
      // Use Google's DNS over HTTPS API to query MX records
      const dnsUrl = `https://dns.google/resolve?name=${encodeURIComponent(domainName)}&type=MX`;
      const response = await fetch(dnsUrl);
      
      if (!response.ok) {
        return 'Error';
      }
      
      const data = await response.json();
      
      // Check if we have MX records in the response
      if (data.Answer && data.Answer.length > 0) {
        // Extract MX records and format them
        const mxRecords = data.Answer
          .map(record => {
            // MX record format: "priority exchange"
            const parts = record.data.split(' ');
            return parts.length > 1 ? parts[1].replace(/\.$/, '') : record.data;
          })
          .join(', ');
        return mxRecords || 'None';
      }
      
      return 'None';
    } catch (error) {
      console.error(`Error looking up MX records for ${domainName}:`, error);
      return 'Error';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cooldown) {
      return; // Prevent submission during cooldown
    }
    
    setError('');
    setDomains([]);
    setDomainMxRecords({});
    setRealm(null);
    setTenantId(null);
    setTenantRegion(null);
    setAdConnectEnabled(null);

    try {
      // Query the worker endpoint
      const apiUrl = `https://sweet-hat-3a19.ourcloudnetwork.workers.dev/?tenant=${encodeURIComponent(domain)}`;
      console.log('Querying worker endpoint:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.domains && Array.isArray(data.domains) && data.domains.length > 0) {
        setDomains(data.domains);
        if (data.realm) {
          setRealm(data.realm);
        }
        
        // Lookup MX records for each domain
        const mxRecordsMap = {};
        for (const d of data.domains) {
          const domainName = typeof d === 'string' ? d : d.name;
          mxRecordsMap[domainName] = 'Loading...';
        }
        setDomainMxRecords(mxRecordsMap);
        
        // Fetch MX records asynchronously
        data.domains.forEach(async (d) => {
          const domainName = typeof d === 'string' ? d : d.name;
          const mxRecords = await lookupMxRecords(domainName);
          setDomainMxRecords(prev => ({
            ...prev,
            [domainName]: mxRecords
          }));
        });
        
        // Fetch tenant ID and region from OpenID configuration
        try {
          const openIdUrl = `https://login.microsoftonline.com/${encodeURIComponent(domain)}/.well-known/openid-configuration`;
          const openIdResponse = await fetch(openIdUrl);
          if (openIdResponse.ok) {
            const openIdData = await openIdResponse.json();
            // Extract tenant ID from the token_endpoint or issuer URL
            // Format: https://login.microsoftonline.com/{tenant-id}/oauth2/v2.0/token
            if (openIdData.token_endpoint) {
              const match = openIdData.token_endpoint.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i);
              if (match) {
                setTenantId(match[1]);
              }
            }
            
            // Extract region information
            if (openIdData.tenant_region_scope) {
              setTenantRegion(openIdData.tenant_region_scope);
            } else if (openIdData.tenant_region_sub_scope) {
              setTenantRegion(openIdData.tenant_region_sub_scope);
            } else {
              // Try to infer region from cloud instance
              const instanceName = data.realm?.cloudInstanceName || '';
              if (instanceName.includes('.us')) {
                setTenantRegion('US Government');
              } else if (instanceName.includes('.cn')) {
                setTenantRegion('China');
              } else if (instanceName.includes('.de')) {
                setTenantRegion('Germany');
              } else if (instanceName === 'microsoftonline.com') {
                setTenantRegion('Worldwide');
              }
            }
          }
        } catch (openIdErr) {
          console.error('Error fetching tenant ID:', openIdErr);
          // Don't fail the whole request if tenant ID fetch fails
        }
        
        // Check for Azure AD Connect by querying the sync service account
        try {
          // Find the .onmicrosoft.com domain from the domains list
          const onMicrosoftDomain = data.domains.find(d => d.endsWith('.onmicrosoft.com') && !d.includes('@'));
          if (onMicrosoftDomain) {
            const syncAccountEmail = `ADToAADSyncServiceAccount@${onMicrosoftDomain}`;
            const adConnectCheckUrl = `https://login.microsoftonline.com/getuserrealm.srf?login=${encodeURIComponent(syncAccountEmail)}&json=1`;
            const adConnectResponse = await fetch(adConnectCheckUrl);
            if (adConnectResponse.ok) {
              const adConnectData = await adConnectResponse.json();
              // If we get a valid response with State 4 or 5, AD Connect is likely configured
              // State 4 = Managed (account exists), State 5 = Federated (account exists)
              if (adConnectData.State === 4 || adConnectData.State === 5) {
                setAdConnectEnabled(true);
              } else {
                setAdConnectEnabled(false);
              }
            } else {
              setAdConnectEnabled(false);
            }
          }
        } catch (adConnectErr) {
          console.error('Error checking AD Connect status:', adConnectErr);
          // Don't fail if AD Connect check fails
        }
      } else {
        setError('No domains found. Please ensure you entered a valid tenant domain (e.g., company.onmicrosoft.com)');
      }
    } catch (err) {
      console.error('Error details:', err);
      setError(`Unable to retrieve domains. Please verify the tenant name is correct and try again.`);
    } finally {
      // Start 3-second cooldown
      setCooldown(true);
      setCooldownTimer(3);
    }
  };

  const exportToCSV = () => {
    if (domains.length === 0) return;

    // Create CSV content
    const csvHeader = 'Domain Name,MX Records\n';
    const csvRows = domains.map(d => {
      const domainName = typeof d === 'string' ? d : d.name;
      const mxRecords = domainMxRecords[domainName] || 'N/A';
      return `"${domainName}","${mxRecords}"`;
    }).join('\n');
    const csvContent = csvHeader + csvRows;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `tenant_domains_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="header-title">
              TENANT<span className="header-subtitle">DOMAINFINDER</span>
            </h1>
            <p className="header-by">
              by <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer">ourcloudnetwork.com</a>
            </p>
          </div>
          <div className="header-right">
            <a href="https://www.linkedin.com/in/danielbradley2/" target="_blank" rel="noopener noreferrer" className="linkedin-button">
              Follow on LinkedIn
            </a>
            <a href="https://x.com/DanielatOCN" target="_blank" rel="noopener noreferrer" className="x-button">
              Follow on X
            </a>
            <a href="https://www.youtube.com/@ourcloudnetwork" target="_blank" rel="noopener noreferrer" className="youtube-button">
              Follow on YouTube
            </a>
          </div>
        </div>
      </header>

      <div className="outage-banner">
        <div className="outage-banner-content">
          <span className="outage-banner-icon">⚠️</span>
          <div className="outage-banner-text">
            <strong>Service Unavailable — Microsoft has patched the domain enumeration technique this tool relied on.</strong>
            <span> Domain lookups are no longer functional. The service is currently down while we investigate alternatives.</span>
          </div>
        </div>
      </div>

      <main className="main-content">
        <div className="container">
          <section className="intro-section">
            <h2>Find All Domains in a Microsoft 365 Tenant</h2>
            <p>
              Enter any domain name associated with a Microsoft 365 tenant to retrieve 
              a complete list of all domains configured within that tenant.
            </p>
          </section>

          <section className="search-section">
            <form onSubmit={handleSubmit} className="search-form">
              <div className="input-group">
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="Enter domain name (e.g., company.com or company.onmicrosoft.com)"
                  className="domain-input"
                  required
                />
                <div className="cf-turnstile" data-sitekey="0x4AAAAAACMuZf-UfqHGmSUU"></div>
                <button type="submit" className="search-button" disabled={true}>
                  Service Unavailable
                </button>
              </div>
            </form>

            {error && (
              <div className="error-message">
                <p>⚠️ {error}</p>
              </div>
            )}

            {realm && (
              <div className="results-section">
                <h3>Tenant Information</h3>
                <table className="realm-table">
                  <tbody>
                    {tenantId && (
                      <tr>
                        <td className="realm-label">Tenant ID:</td>
                        <td className="realm-value">{tenantId}</td>
                      </tr>
                    )}
                    {tenantRegion && (
                      <tr>
                        <td className="realm-label">Tenant Region:</td>
                        <td className="realm-value">{tenantRegion}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="realm-label">Default Domain:</td>
                      <td className="realm-value">{realm.domainName}</td>
                    </tr>
                    <tr>
                      <td className="realm-label">Tenant Brand:</td>
                      <td className="realm-value">{realm.federationBrandName}</td>
                    </tr>
                    <tr>
                      <td className="realm-label">Namespace Type:</td>
                      <td className="realm-value">{realm.namespaceType}</td>
                    </tr>
                    {realm.desktopSsoEnabled !== undefined && (
                      <tr>
                        <td className="realm-label">Seamless SSO:</td>
                        <td className="realm-value">
                          {realm.desktopSsoEnabled ? 'Enabled' : 'Disabled'}
                        </td>
                      </tr>
                    )}
                    {adConnectEnabled !== null && (
                      <tr>
                        <td className="realm-label">Azure AD Connect:</td>
                        <td className={`realm-value ${adConnectEnabled ? 'realm-value-enabled' : 'realm-value-disabled'}`}>
                          {adConnectEnabled ? '✓ Detected' : '✗ Not Detected'}
                        </td>
                      </tr>
                    )}
                    {realm.authURL && (
                      <tr>
                        <td className="realm-label">Auth URL:</td>
                        <td className="realm-value">{realm.authURL}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {domains.length > 0 && (
              <div className="results-section">
                <div className="results-header">
                  <h3>Domains Found ({domains.length})</h3>
                  <button onClick={exportToCSV} className="export-button">
                    📥 Export to CSV
                  </button>
                </div>
                
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Domain Name</th>
                      <th>MX Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((d, index) => {
                      const domainName = typeof d === 'string' ? d : d.name;
                      const mxRecords = domainMxRecords[domainName] || 'Loading...';
                      return (
                        <tr key={index}>
                          <td>{domainName}</td>
                          <td>{mxRecords}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="about-section">
            <h2>About This Tool</h2>
            <p>
              This tool retrieves all registered email domains for a Microsoft 365 tenant. 
              Simply enter any domain associated with the tenant to discover all configured domains.
            </p>
            <p style={{ marginTop: '1rem' }}>
              <strong>How to use:</strong> Enter a tenant domain (such as company.onmicrosoft.com 
              or any custom domain like company.com) and click "Find Domains" to retrieve the complete list.
            </p>
          </section>
        </div>
      </main>

      <footer className="footer">
        <p>
          Copyright © 2026 TenantDomainFinder | Powered by{' '}
          <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer">
            ourcloudnetwork.com
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
